import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowDown, ArrowUp, CalendarClock, Download, RotateCcw, Search, ShieldCheck,
} from "lucide-react";

/**
 * Lo scadenzario del personale: tutto quello che scade, in una tabella sola.
 *
 * Serve a prenotare visite e corsi prima che sia tardi. Per questo accanto a
 * quello che scade c'è anche quello che manca del tutto — un corso mai fatto è
 * un corso da prenotare esattamente come uno in scadenza — e quello che c'è ma
 * senza data, che è un buco nel controllo: finché quella casella resta vuota,
 * di quella persona non si sa quando scade.
 *
 * Si comporta come un foglio di calcolo: intestazione ferma, colonne che si
 * ordinano con un tocco, filtri che si sommano, ed esce in Excel esattamente
 * quello che si sta guardando.
 */
const COLORI = {
  mancante: "bg-red-50 text-red-700 border-red-200",
  scaduto: "bg-red-50 text-red-700 border-red-200",
  entro_30: "bg-orange-50 text-orange-700 border-orange-200",
  entro_90: "bg-yellow-50 text-yellow-800 border-yellow-200",
  in_regola: "bg-emerald-50 text-emerald-700 border-emerald-200",
  senza_data: "bg-slate-100 text-slate-600 border-slate-200",
};

const itDate = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "—");

/** "fra 20 giorni", "scaduto da 5 giorni": il numero da solo non si legge. */
const quando = (giorni) => {
  if (giorni === null || giorni === undefined) return "—";
  if (giorni < 0) return `scaduto da ${-giorni} gg`;
  if (giorni === 0) return "scade oggi";
  return `fra ${giorni} gg`;
};

export default function Scadenzario() {
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [cerca, setCerca] = useState("");
  const [stati, setStati] = useState([]);
  const [dipendente, setDipendente] = useState("");
  const [tipo, setTipo] = useState("");
  const [soloObbligatori, setSoloObbligatori] = useState(false);
  const [ordine, setOrdine] = useState({ campo: null, verso: 1 });

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get("/scadenzario");
      setDati(data);
    } catch {
      toast.error("Errore nel caricamento dello scadenzario");
    } finally { setCarico(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const righe = useMemo(() => {
    if (!dati) return [];
    const q = cerca.trim().toLowerCase();
    let out = dati.righe.filter((r) =>
      (stati.length === 0 || stati.includes(r.stato)) &&
      (!dipendente || r.dipendente === dipendente) &&
      (!tipo || r.tipo === tipo) &&
      (!soloObbligatori || r.obbligatorio) &&
      (!q || `${r.dipendente} ${r.tipo} ${r.titolo} ${r.mansione}`.toLowerCase().includes(q)));

    if (ordine.campo) {
      const vuoto = ordine.campo === "giorni" ? 999999 : "";
      out = [...out].sort((a, b) => {
        const x = a[ordine.campo] ?? vuoto;
        const y = b[ordine.campo] ?? vuoto;
        if (x === y) return a.dipendente.localeCompare(b.dipendente);
        return (x > y ? 1 : -1) * ordine.verso;
      });
    }
    return out;
  }, [dati, cerca, stati, dipendente, tipo, soloObbligatori, ordine]);

  const ordinaPer = (campo) =>
    setOrdine((o) => ({ campo, verso: o.campo === campo ? -o.verso : 1 }));

  const toggleStato = (codice) =>
    setStati((s) => (s.includes(codice) ? s.filter((x) => x !== codice) : [...s, codice]));

  const azzera = () => {
    setCerca(""); setStati([]); setDipendente(""); setTipo("");
    setSoloObbligatori(false); setOrdine({ campo: null, verso: 1 });
  };

  const esporta = async () => {
    // Esce esattamente quello che si sta guardando: i filtri viaggiano con la
    // richiesta, così il foglio e lo schermo non raccontano due cose diverse.
    const p = new URLSearchParams();
    stati.forEach((s) => p.append("stato", s));
    if (dipendente) p.append("dipendente", dipendente);
    if (tipo) p.append("tipo", tipo);
    if (soloObbligatori) p.set("solo_obbligatori", "true");
    const qs = p.toString();
    try {
      const r = await authFetch(`/scadenzario/xlsx${qs ? `?${qs}` : ""}`);
      if (!r.ok) throw new Error();
      const cd = r.headers.get("content-disposition") || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || "scadenzario_SGM.xlsx";
      const u = window.URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
      toast.success("Scadenzario scaricato");
    } catch { toast.error("Errore nel download"); }
  };

  if (carico) return <p className="text-sm text-slate-500">Caricamento...</p>;
  if (!dati) return null;

  const nomiStato = Object.fromEntries(dati.stati.map((s) => [s.codice, s.nome]));
  const filtrato = righe.length !== dati.righe.length;

  const intestazione = (campo, testo, classe = "text-left") => (
    <th className={`${classe} p-2 font-semibold whitespace-nowrap`}>
      <button type="button" onClick={() => ordinaPer(campo)}
              className="inline-flex items-center gap-1 hover:text-orange-600">
        {testo}
        {ordine.campo === campo && (ordine.verso === 1
          ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </button>
    </th>
  );

  return (
    <div className="space-y-5" data-testid="scadenzario-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Dipendenti</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Scadenzario</h1>
          <p className="text-sm text-slate-500 mt-1">
            Tutto quello che scade, e quello che manca da prenotare · aggiornato
            al {itDate(dati.aggiornato_al)}
          </p>
        </div>
        <Button onClick={esporta} data-testid="esporta-scadenzario"
                className="bg-orange-500 hover:bg-orange-600 text-white h-12">
          <Download className="w-4 h-4 mr-2" />
          {filtrato ? "Esporta quello che vedi" : "Esporta in Excel"}
        </Button>
      </div>

      {/* Le pastiglie sono anche il filtro: si tocca lo stato e la tabella si
          stringe su quello. Più d'uno si somma. */}
      <div className="flex gap-2 flex-wrap">
        {dati.stati.map((s) => {
          const n = dati.conteggi[s.codice] || 0;
          if (!n) return null;
          const attivo = stati.includes(s.codice);
          return (
            <button key={s.codice} type="button" onClick={() => toggleStato(s.codice)}
                    data-testid={`stato-${s.codice}`}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                      COLORI[s.codice]} ${attivo ? "ring-2 ring-offset-1 ring-orange-400" : ""}`}>
              {s.nome} · {n}
            </button>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
                     placeholder="Cerca una persona o un documento"
                     className="pl-9" data-testid="cerca-scadenzario" />
            </div>
            <select value={dipendente} onChange={(e) => setDipendente(e.target.value)}
                    data-testid="filtro-dipendente"
                    className="h-10 border border-slate-200 rounded px-3 text-sm min-w-[180px]">
              <option value="">Tutti i dipendenti</option>
              {dati.dipendenti.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}
                    data-testid="filtro-tipo"
                    className="h-10 border border-slate-200 rounded px-3 text-sm min-w-[180px]">
              <option value="">Tutte le tipologie</option>
              {dati.tipi.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap">
              <input type="checkbox" checked={soloObbligatori}
                     onChange={(e) => setSoloObbligatori(e.target.checked)}
                     data-testid="solo-obbligatori" />
              Solo obbligatori
            </label>
            {filtrato && (
              <Button variant="outline" size="sm" onClick={azzera} data-testid="azzera-filtri">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Azzera
              </Button>
            )}
          </div>

          <div className="text-xs text-slate-500">
            {righe.length} righe{filtrato && ` di ${dati.righe.length}`}
          </div>

          {righe.length === 0 ? (
            <p className="text-sm text-emerald-700 flex items-center gap-2 py-8 justify-center">
              <ShieldCheck className="w-5 h-5" /> Niente da mostrare con questi filtri.
            </p>
          ) : (
            <div className="overflow-auto max-h-[65vh] border border-slate-200 rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 sticky top-0 z-10">
                  <tr>
                    {intestazione("dipendente", "Dipendente")}
                    {intestazione("mansione", "Mansione")}
                    {intestazione("tipo", "Documento")}
                    {intestazione("scadenza", "Scadenza")}
                    {intestazione("giorni", "Quando", "text-left")}
                    {intestazione("stato", "Stato")}
                  </tr>
                </thead>
                <tbody>
                  {righe.map((r, i) => (
                    <tr key={`${r.certificate_id || r.tipo}-${r.employee_id}-${i}`}
                        className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-2 font-medium whitespace-nowrap">{r.dipendente}</td>
                      <td className="p-2 text-slate-500 whitespace-nowrap">{r.mansione}</td>
                      <td className="p-2">
                        {r.tipo}
                        {r.obbligatorio && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">
                            obbligatorio
                          </span>
                        )}
                      </td>
                      <td className="p-2 tabular-nums whitespace-nowrap">{itDate(r.scadenza)}</td>
                      <td className="p-2 text-slate-600 whitespace-nowrap">{quando(r.giorni)}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${COLORI[r.stato]}`}>
                          {nomiStato[r.stato] || r.stato}
                        </span>
                        {/* Un aggiornamento da lavoratore oltre la sua data
                            ma in regola lo e' perche' il preposto lo
                            sostituisce: senza dirlo sembra un errore. */}
                        {r.coperto_da && (
                          <div className="text-[10px] text-slate-500 mt-0.5 whitespace-nowrap">
                            coperto dall&apos;aggiornamento preposto, fino al {itDate(r.coperto_fino)}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-500 flex items-start gap-2">
        <CalendarClock className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          «Manca» vuol dire che quel documento non c&apos;è per niente, e va
          prenotato. «Senza scadenza indicata» vuol dire che il documento c&apos;è
          ma non si sa fino a quando vale: finché quella data resta vuota, quella
          persona non entra nei controlli.
        </span>
      </p>
    </div>
  );
}
